import { Site } from '@snapchat/mw-common';
import { snapPersonasDisplayTextMap } from '@snapchat/mw-contentful-schema';
import { BackgroundType, CookieModal as CookiePopupGlobal } from '@snapchat/mw-global-components';
import { BackgroundColor } from '@snapchat/snap-design-system-marketing';
import type React from 'react';
import { useContext } from 'react';

import { AppContext } from '../../AppContext';
import { Config } from '../../config';
import {
  logError,
  logEvent,
  onMarketingTrackingAccepted,
  onPerformanceTrackingAccepted,
  SubscribedEventType,
} from '../../helpers/logging';
import { handlePartnerstackQueryParam } from '../../sites/for-business/utils/handlePartnerstackQueryParam';
import { getPersonasCookie, setPersonasCookie } from '../../utils/persona/personaUtils';
import { ShopifyContext } from '../ShopifyProvider';
import { submitShopifyTrackingConsent } from '../ShopifyProvider/submitShopifyTrackingConsent';

export interface CookiePopupProps {
  isClient: boolean;
  backgroundColor?: BackgroundColor;
  globalPrivacyControl?: boolean;
}

const defaultBackgroundColor = BackgroundColor.White;

// TODO: Grab isClient from Config, grab globalPrivacyControl from AppContext,
// grab backgroundColor from Config.theme.
export const CookiePopup: React.FC<CookiePopupProps> = ({
  isClient,
  backgroundColor = defaultBackgroundColor,
  globalPrivacyControl,
}) => {
  const { cookieManager, personasRef } = useContext(AppContext);
  const { store } = useContext(ShopifyContext);

  // Return nothing for SSR
  if (!isClient) return null;

  const backgroundType =
    backgroundColor === BackgroundColor.Black ? BackgroundType.DarkMode : BackgroundType.LightMode;

  // For non-production environments (localhost, staging sites) - specify empty string so that cookies will can be set.
  const cookieDomain =
    !Config.isDeploymentTypeProd || !Config.isCompilationModeProd ? '' : undefined;

  return (
    <CookiePopupGlobal
      cookieDomain={cookieDomain}
      backgroundType={backgroundType}
      onComplete={event => {
        if (event.cookieAcceptance.Marketing) {
          onMarketingTrackingAccepted().catch(error =>
            logError({ component: 'CookiePopup', error })
          );
        }

        if (event.cookieAcceptance.Performance) {
          onPerformanceTrackingAccepted().catch(error =>
            logError({ component: 'CookiePopup', error })
          );
        }

        if (event.cookieAcceptance.Preferences) {
          // Set the cookie with the app state personas if they are available,
          // If undefined, this is a no-op because the cookie will not be set.
          setPersonasCookie(cookieManager, personasRef?.current ?? undefined);
        }

        // Pre-seed Shopify consent cookies so users skip Shopify's own banner at checkout.
        // Fire-and-forget: Shopify's banner remains as a fallback if this fails.
        if (store) {
          void submitShopifyTrackingConsent({
            cookieAcceptance: event.cookieAcceptance,
            store,
            saleOfData: !event.isGlobalPrivacyControlApplied,
          }).catch(error => logError({ component: 'CookiePopup', error }));
        }

        // Special Logic for FOR BIZ Only to handle partner stack query params
        if (Config.site === Site.FOR_BUSINESS) {
          handlePartnerstackQueryParam?.(cookieManager, !!event.cookieAcceptance.Marketing);

          // Log the snap personas if the user has accepted preferences cookies
          // We DON'T use the personas from AppContext here because that is only set
          // when an experiment is run and the personas are updated.
          if (event.cookieAcceptance.Preferences) {
            const personasCookieValue = getPersonasCookie(cookieManager);
            const snapPersonas = personasCookieValue?.snap ?? [];

            for (const snapPersona of snapPersonas) {
              const snapPersonaDisplayText = snapPersonasDisplayTextMap[snapPersona];

              if (snapPersonaDisplayText) {
                logEvent({
                  subscribedEventType: SubscribedEventType.INTERNAL,
                  eventCategory: 'SnapPersona',
                  eventAction: 'Get',
                  eventLabel: snapPersonaDisplayText,
                });
              }
            }
          }
        }
      }}
      globalPrivacyControl={globalPrivacyControl}
    />
  );
};
